import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import { getCurrentOperatorActorId } from '../../utils/operatorSession'
import { createId } from '../../utils/id'
import { waterParametersToAsk } from '../../utils/unitPlans'
import { useLots } from '../../hooks/useLots'
import {
  deleteWaterTableRowAndSync,
  loadWaterTableRows,
  readWaterTableRowSyncStatus,
  retryWaterTableRowSync,
  upsertWaterTableRowAndSync,
  type WaterDataRow
} from '../../utils/waterTable'
import OperatorGate from './OperatorGate'
import CrewModeFarmBar from './CrewModeFarmBar'
import { useCrewModeFarm } from './useCrewModeFarm'
import { isWaterLogCrewModeEnabled } from './waterLogFeatureFlag'
import { recordCrewEvent } from './crewModeTelemetry'
import { resolvePondLots } from './pondLotResolution'
import { useCrewLocation } from './useCrewLocation'
import { CrewLocationHeader, CrewLocationPicker, CrewNextLocationButton } from './CrewLocationPicker'
import { buildOperatorUndoAudit } from './crewCaptureHelpers'
import { isUnsyncedBadgeState, useRecheckWhileUnsynced } from './useRecheckWhileUnsynced'
import {
  WATER_CAPTURE_PARAMETER_RANGES,
  buildWaterCaptureRow,
  findLikelyDuplicateReading,
  validateWaterCaptureValue,
  type WaterCaptureParameter
} from './waterCaptureValidation'

// WaterLog v0 PRD — Stage 4. Charter U-2 interaction budget: 3 taps warm
// (parameter chip -> value entry -> confirm), 4 taps cold (+ production
// unit selector). Charter O-5: Confirm never waits on network — it writes
// locally via the existing waterTable.ts outbox path and shows a state
// immediately. Charter D-4: capturedByActorId is stamped from the active
// OperatorGate session, never left to the server's device-fallback.

type SyncBadgeState = 'local' | 'pending' | 'synced' | 'error'

const chipButtonStyle: React.CSSProperties = {
  minWidth: 56,
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
  padding: '8px 12px'
}

const primaryButtonStyle: React.CSSProperties = {
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #1d4ed8',
  backgroundColor: '#1d4ed8',
  color: 'white',
  fontWeight: 800,
  fontSize: 16,
  cursor: 'pointer',
  width: '100%'
}

const parameterLabelKey: Record<WaterCaptureParameter, TranslationKey> = {
  temperature: 'crewMode.water.parameter.temperature',
  ph: 'crewMode.water.parameter.ph',
  dissolvedOxygen: 'crewMode.water.parameter.dissolvedOxygen',
  salinity: 'crewMode.water.parameter.salinity',
  flow: 'crewMode.water.parameter.flow'
}

const syncBadgeStyle: Record<SyncBadgeState, React.CSSProperties> = {
  local: { backgroundColor: '#f3f4f6', color: '#374151' },
  pending: { backgroundColor: '#fffbeb', color: '#92400e' },
  synced: { backgroundColor: '#f0fdf4', color: '#166534' },
  error: { backgroundColor: '#fef2f2', color: '#991b1b' }
}

const syncBadgeLabelKey: Record<SyncBadgeState, TranslationKey> = {
  local: 'crewMode.water.sync.local',
  pending: 'crewMode.water.sync.pending',
  synced: 'crewMode.water.sync.synced',
  error: 'crewMode.water.sync.error'
}

type CapturedReadingStatus = {
  rowId: string
  parameter: WaterCaptureParameter
  value: string
  locationLabel: string
  state: SyncBadgeState
}

export default function WaterLogCapture() {
  const t = useT()
  const { farms, farmId, selectFarm } = useCrewModeFarm()
  const [parameter, setParameter] = useState<WaterCaptureParameter | null>(null)
  const [valueDraft, setValueDraft] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<WaterDataRow | null>(null)
  const [existingRows, setExistingRows] = useState<WaterDataRow[]>([])
  const [lastReading, setLastReading] = useState<CapturedReadingStatus | null>(null)
  const valueInputRef = useRef<HTMLInputElement>(null)

  // Field Bundle v0 §3 items 1–4: shared location; water picks a unit (D-2).
  const crewLocation = useCrewLocation({ stream: 'water', farmId, farms })
  const { location } = crewLocation
  const productionUnitId = location?.productionUnitId ?? ''

  // Office Plans v0 §3.4: only the parameters the office asked for this unit,
  // in its order; all four when none were set.
  const askedParameters = useMemo<WaterCaptureParameter[]>(() => {
    const unit = farms.find((farm) => farm.id === farmId)?.productionUnits?.find((candidate) => candidate.id === productionUnitId)
    return waterParametersToAsk(unit)
  }, [farms, farmId, productionUnitId])

  useEffect(() => {
    setParameter((current) => (current && !askedParameters.includes(current) ? null : current))
  }, [askedParameters])

  // Silent, invisible-to-the-operator lot resolution — never shown as a
  // picker (PRD §4 "no lot exposure" stays true). Only used to stamp the
  // plain-text `lot` label the biomass/SFR projection engine requires
  // (mortalityProjection.ts's normalizeProjectionWaterLogs drops any water
  // reading with no `lot`). A pond with more than one active lot at once
  // is left unlabeled, same as before — the admin Water screen's per-row
  // lot picker remains the correction path for that rare case.
  const { lots } = useLots({ farmId, productionUnitId, enabled: Boolean(farmId && productionUnitId) })
  const activeLot = useMemo(() => {
    const resolved = resolvePondLots(lots)
    return resolved.kind === 'single' ? resolved.lot : undefined
  }, [lots])

  useEffect(() => {
    if (!farmId) return
    recordCrewEvent('capture.start', { stream: 'water', farmId })
  }, [farmId])

  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setExistingRows([])
      return
    }
    setExistingRows(loadWaterTableRows({ farmId, productionUnitId }))
  }, [farmId, productionUnitId])

  const handleSelectParameter = (nextParameter: WaterCaptureParameter) => {
    setParameter(nextParameter)
    setValueDraft('')
    setValidationError(null)
    setDuplicateWarning(null)
    // Charter U-2: value entry is the very next tap in the warm path —
    // auto-focus so the operator doesn't need an extra tap to reach it.
    requestAnimationFrame(() => valueInputRef.current?.focus())
  }

  async function handleConfirm() {
    if (!parameter || !farmId || !productionUnitId) return

    const validation = validateWaterCaptureValue(parameter, valueDraft)
    if (!validation.ok) {
      setValidationError(
        validation.reason === 'invalid'
          ? t('crewMode.water.error.invalidValue')
          : t('crewMode.water.error.outOfRange', {
            min: String(WATER_CAPTURE_PARAMETER_RANGES[parameter].min),
            max: String(WATER_CAPTURE_PARAMETER_RANGES[parameter].max),
            unit: WATER_CAPTURE_PARAMETER_RANGES[parameter].unit
          })
      )
      return
    }
    setValidationError(null)

    const recordedAtIso = new Date().toISOString()
    const duplicate = findLikelyDuplicateReading({ parameter, recordedAtIso, existingRows })
    if (duplicate && duplicateWarning?.id !== duplicate.id) {
      // First Confirm tap surfaces the warning; a second tap on the same
      // candidate proceeds — PRD AC #9: warn, never hard-block.
      setDuplicateWarning(duplicate)
      return
    }

    const numericValue = Number(valueDraft.trim().replace(',', '.'))
    const row = buildWaterCaptureRow({
      id: createId('water'),
      parameter,
      numericValue,
      recordedAtIso,
      capturedByActorId: getCurrentOperatorActorId(),
      lot: activeLot?.lotKey
    })

    const nextRows = [...existingRows, row]
    const result = await upsertWaterTableRowAndSync({ farmId, productionUnitId }, nextRows, row, { source: 'crewMode' })
    setExistingRows(nextRows)

    const initialState: SyncBadgeState = result.syncState === 'synced' ? 'synced' : result.syncState === 'pending' ? 'pending' : 'local'

    recordCrewEvent('capture.confirm', {
      stream: 'water',
      farmId,
      syncState: initialState,
      capturedByActorId: getCurrentOperatorActorId()
    })

    setLastReading({ rowId: row.id, parameter, value: valueDraft.trim(), locationLabel: location?.label ?? '', state: initialState })

    setParameter(null)
    setValueDraft('')
    setDuplicateWarning(null)

    result.syncPromise?.then(async () => {
      const status = await readWaterTableRowSyncStatus([row.id])
      if (status.blockedCount > 0) {
        recordCrewEvent('capture.error', { stream: 'water', farmId })
      }
      setLastReading((prev) => (prev?.rowId === row.id
        ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
        : prev))
    }).catch(() => {
      recordCrewEvent('capture.error', { stream: 'water', farmId })
      setLastReading((prev) => (prev?.rowId === row.id ? { ...prev, state: 'error' } : prev))
    })
  }

  // Field Bundle v0 §3 item 6: undo the operator's own last reading.
  const handleUndo = async () => {
    if (!lastReading || !farmId || !productionUnitId) return
    const rowId = lastReading.rowId
    const remaining = existingRows.filter((candidate) => candidate.id !== rowId)
    await deleteWaterTableRowAndSync(
      { farmId, productionUnitId },
      remaining,
      rowId,
      buildOperatorUndoAudit(t('crewCorrection.reason.operatorUndo'))
    )
    setExistingRows(remaining)
    setLastReading(null)
    recordCrewEvent('capture.undo', { stream: 'water', farmId })
  }

  const handleRetry = async () => {
    if (!lastReading) return
    await retryWaterTableRowSync([lastReading.rowId])
    const status = await readWaterTableRowSyncStatus([lastReading.rowId])
    setLastReading((prev) => (prev?.rowId === lastReading.rowId
      ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
      : prev))
  }

  // Findings #137: flip the badge once the background flush sends it.
  useRecheckWhileUnsynced(isUnsyncedBadgeState(lastReading?.state), async () => {
    if (!lastReading) return
    const rowId = lastReading.rowId
    const status = await readWaterTableRowSyncStatus([rowId])
    setLastReading((prev) => (prev?.rowId === rowId
      ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : prev.state) }
      : prev))
  })

  if (farmId && !isWaterLogCrewModeEnabled(farmId)) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />
        <div data-testid="water-capture-flag-disabled" style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
          {t('crewMode.water.notEnabledForFarm')}
        </div>
      </div>
    )
  }

  return (
    <OperatorGate>
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />

        <CrewLocationHeader stream="water" location={location} onChange={crewLocation.openPicker} />
        <CrewLocationPicker
          stream="water"
          open={crewLocation.pickerOpen}
          options={crewLocation.options}
          currentKey={location?.key}
          onChoose={(option) => {
            crewLocation.choose(option)
            setLastReading(null)
          }}
          onClose={crewLocation.closePicker}
        />

        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>{t('crewMode.water.selectParameter')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {askedParameters.map((candidateParameter) => (
              <button
                key={candidateParameter}
                type="button"
                data-testid={`water-capture-parameter-${candidateParameter}`}
                onClick={() => handleSelectParameter(candidateParameter)}
                style={{
                  ...chipButtonStyle,
                  borderColor: parameter === candidateParameter ? '#1d4ed8' : '#d1d5db',
                  backgroundColor: parameter === candidateParameter ? '#eff6ff' : 'white'
                }}
              >
                {t(parameterLabelKey[candidateParameter])}
              </button>
            ))}
          </div>
        </div>

        {parameter && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
              {t(parameterLabelKey[parameter])} ({WATER_CAPTURE_PARAMETER_RANGES[parameter].unit || t('crewMode.water.parameter.phUnit')})
            </label>
            <input
              ref={valueInputRef}
              data-testid="water-capture-value-input"
              type="text"
              inputMode="decimal"
              enterKeyHint="done"
              value={valueDraft}
              onChange={(event) => {
                setValueDraft(event.target.value)
                setValidationError(null)
                setDuplicateWarning(null)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void handleConfirm()
                }
              }}
              style={{
                width: '100%',
                minHeight: 56,
                padding: '8px 14px',
                borderRadius: 10,
                border: validationError ? '2px solid #dc2626' : '1px solid #d1d5db',
                fontSize: 24,
                fontWeight: 700
              }}
            />
            {validationError && (
              <div data-testid="water-capture-validation-error" style={{ marginTop: 6, color: '#dc2626', fontWeight: 700 }}>
                {validationError}
              </div>
            )}
            {duplicateWarning && (
              <div data-testid="water-capture-duplicate-warning" style={{ marginTop: 6, color: '#92400e', fontWeight: 700 }}>
                {t('crewMode.water.warning.duplicate')}
              </div>
            )}
          </div>
        )}

        {parameter && (
          <button
            type="button"
            data-testid="water-capture-confirm"
            onClick={() => void handleConfirm()}
            style={primaryButtonStyle}
          >
            {duplicateWarning ? t('crewMode.water.confirmAnyway') : t('crewMode.water.confirm')}
          </button>
        )}

        {lastReading && (
          <div
            data-testid="water-capture-last-reading"
            style={{
              padding: 12,
              borderRadius: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              ...syncBadgeStyle[lastReading.state]
            }}
          >
            <span style={{ fontWeight: 700 }}>
              <span data-testid="water-capture-last-location" style={{ display: 'block', fontSize: 13 }}>{lastReading.locationLabel}</span>
              {t(parameterLabelKey[lastReading.parameter])}: {lastReading.value}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                data-testid="water-capture-undo"
                onClick={() => void handleUndo()}
                style={{ ...chipButtonStyle, minHeight: 44, minWidth: 44, fontSize: 13, padding: '4px 10px' }}
              >
                {t('crewMode.undo')}
              </button>
              <span data-testid="water-capture-sync-state" style={{ fontWeight: 800 }}>
                {t(syncBadgeLabelKey[lastReading.state])}
              </span>
              {lastReading.state === 'error' && (
                <button
                  type="button"
                  data-testid="water-capture-retry"
                  onClick={() => void handleRetry()}
                  style={{ ...chipButtonStyle, minHeight: 40, minWidth: 40, fontSize: 12, padding: '4px 10px' }}
                >
                  {t('crewMode.water.retry')}
                </button>
              )}
            </span>
          </div>
        )}

        {lastReading && (
          <CrewNextLocationButton
            next={crewLocation.next}
            onGo={(option) => {
              crewLocation.choose(option)
              setLastReading(null)
            }}
          />
        )}
      </div>
    </OperatorGate>
  )
}
